/**
 * TrialBanner.jsx — MK-004 30-day trial countdown banner (PR_RUN_021)
 *
 * Persistent banner across all pages per TRL-011. Renders nothing for
 * paid tiers, BOOK_ACCESS, and pre-MK-004 legacy accounts. For TRIAL
 * and EXPIRING_SOON states it shows the countdown with urgency tier
 * (TRL-012): neutral > 5d, amber ≤ 5d, red ≤ 2d. Dismissible per
 * session (sessionStorage); reappears on next login.
 *
 * Calls GET /api/trial/status — caches the result for 60s in the
 * component to keep the <50ms NFR (TRL-009).
 */
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';

const STATUS_CACHE_MS = 60_000;
const DISMISS_KEY = 'charstan_trial_banner_dismissed';

export default function TrialBanner() {
  const { t } = useTranslation('trial');
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(
    typeof window !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1',
  );

  useEffect(() => {
    let cancelled = false;
    let timer = null;
    async function fetchStatus() {
      try {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/trial/status`;
        const res = await fetch(url, { credentials: 'include' });
        if (!res.ok) return;
        const data = await res.json();
        if (!cancelled) setStatus(data);
      } catch {
        // Silent — banner is purely informational. Errors are surfaced
        // by the underlying API error gate, not this banner.
      }
      if (!cancelled) timer = setTimeout(fetchStatus, STATUS_CACHE_MS);
    }
    fetchStatus();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, []);

  if (!status || dismissed) return null;

  const { account_type, days_remaining, book_access_code } = status;

  // Book-access users get a different banner (positive, not urgency-driven).
  if (account_type === 'BOOK_ACCESS' && book_access_code) {
    return (
      <div
        data-testid="trial-banner-book-access"
        className="w-full bg-emerald-50 text-emerald-900 border-b border-emerald-200 px-4 py-2 flex items-center justify-between text-sm"
      >
        <span>{t('banner.book_access')}</span>
        <button
          aria-label={t('banner.dismiss')}
          onClick={() => { sessionStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); }}
          data-testid="trial-banner-dismiss"
          className="p-1 hover:bg-emerald-100 rounded"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  if (!['TRIAL', 'EXPIRING_SOON', 'EXPIRED'].includes(account_type)) return null;

  // Urgency tier (TRL-012).
  let toneClasses = 'bg-slate-50 text-slate-900 border-slate-200';  // neutral
  if (days_remaining !== null && days_remaining <= 5) {
    toneClasses = 'bg-amber-50 text-amber-900 border-amber-300';
  }
  if (days_remaining !== null && days_remaining <= 2) {
    toneClasses = 'bg-red-50 text-red-900 border-red-300';
  }
  if (account_type === 'EXPIRED') {
    toneClasses = 'bg-red-100 text-red-950 border-red-400';
  }

  let message;
  if (account_type === 'EXPIRED') {
    message = t('banner.expired');
  } else if (days_remaining === 1) {
    message = t('banner.active_one_day');
  } else if (account_type === 'EXPIRING_SOON' && days_remaining === 2) {
    message = t('banner.expiring_two_days');
  } else if (account_type === 'EXPIRING_SOON') {
    message = t('banner.expiring_soon', { count: days_remaining });
  } else {
    message = t('banner.active_days_left', { count: days_remaining });
  }

  return (
    <div
      data-testid="trial-banner"
      className={`w-full ${toneClasses} border-b px-4 py-2 flex items-center justify-between text-sm`}
    >
      <div className="flex-1 flex items-center gap-3">
        <span data-testid="trial-banner-message">{message}</span>
        <button
          onClick={() => navigate('/upgrade')}
          data-testid="trial-banner-upgrade-cta"
          className="px-3 py-1 bg-slate-900 text-white text-xs font-semibold rounded hover:bg-slate-800 transition"
        >
          {t('banner.upgrade_cta')}
        </button>
      </div>
      <button
        aria-label={t('banner.dismiss')}
        onClick={() => { sessionStorage.setItem(DISMISS_KEY, '1'); setDismissed(true); }}
        data-testid="trial-banner-dismiss"
        className="p-1 hover:bg-black/10 rounded"
      >
        <X size={16} />
      </button>
    </div>
  );
}
